import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import type { AckResult, ClientToServerEvents, ServerToClientEvents } from '@bn/shared';
import { useAuth } from './auth';

export type ClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

interface SocketValue {
  socket: ClientSocket | null;
  connected: boolean;
}

const SocketContext = createContext<SocketValue>({ socket: null, connected: false });

export function SocketProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [socket, setSocket] = useState<ClientSocket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!user) {
      setSocket(null);
      setConnected(false);
      return;
    }

    const instance: ClientSocket = io({
      path: '/socket.io',
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    instance.on('connect', () => setConnected(true));
    instance.on('disconnect', () => setConnected(false));
    setSocket(instance);

    return () => {
      instance.removeAllListeners();
      instance.close();
    };
  }, [user?.id]);

  const value = useMemo(() => ({ socket, connected }), [socket, connected]);
  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket(): SocketValue {
  return useContext(SocketContext);
}

/** Émet un évènement et attend l'accusé de réception du serveur. */
export function ask<T>(
  socket: ClientSocket,
  event: keyof ClientToServerEvents,
  payload: unknown,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeout = setTimeout(
      () => reject(new Error('Le serveur ne répond pas.')),
      10_000,
    );
    (socket.emit as (...args: unknown[]) => void)(
      event,
      payload,
      (result: AckResult<T>) => {
        clearTimeout(timeout);
        if (result?.ok) resolve(result.data);
        else reject(new Error(result?.error ?? 'Erreur inconnue.'));
      },
    );
  });
}
